import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useQuery, getDailyBriefing } from 'wasp/client/operations';
import { Button } from '../components/ui';
import { getBrowserOrigin, shareOrCopyUrl } from '../client/shareLinks';
import {
  buildAbsoluteShareUrl,
  buildTodaySharePath,
} from '../shared/shareRoutes';
import { BriefingDateNavigator } from './components/BriefingDateNavigator';
import { BriefingStoryCard } from './components/BriefingStoryCard';
import { BriefingStatusState } from './components/BriefingStatusState';
import { formatDisplayDate } from './types';

const BASE_URL = 'https://optimistic-news.com';

export function TodayPage() {
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date') ?? undefined;
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const shareFeedbackTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    };
  }, []);

  const showShareFeedback = (msg: string) => {
    if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    setShareFeedback(msg);
    shareFeedbackTimer.current = setTimeout(() => setShareFeedback(null), 2000);
  };

  const { data, isLoading, error } = useQuery(
    getDailyBriefing,
    { date: dateParam },
    { staleTime: 2 * 60 * 1000 },
  );

  const canonicalUrl = dateParam
    ? `${BASE_URL}/today?date=${dateParam}`
    : `${BASE_URL}/today`;

  const pageTitle =
    data && data.status !== 'missing'
      ? `Daily Briefing ${formatDisplayDate(data.issueDate)} - Optimistic News`
      : 'Daily Positive Briefing - Optimistic News';

  const pageDescription =
    data?.status === 'published'
      ? data.introText ??
        `Your daily dose of uplifting news for ${data.issueDate}. Five positive stories selected from around the world.`
      : 'Your daily dose of uplifting news. Five positive stories selected from around the world each day.';

  const heroStory = data?.status === 'published' ? data.stories[0] : null;
  const remainingStories =
    data?.status === 'published' ? data.stories.slice(1) : [];
  const activeShareIssueDate =
    dateParam ?? (data?.status === 'published' ? data.issueDate : undefined);

  const handleShareClick = () => {
    if (!activeShareIssueDate) {
      return;
    }

    const shareUrl = buildAbsoluteShareUrl(
      getBrowserOrigin(),
      buildTodaySharePath(activeShareIssueDate),
    );

    void shareOrCopyUrl({
      title: pageTitle,
      text: pageDescription,
      url: shareUrl,
    })
      .then((result) => {
        showShareFeedback(result === 'copied' ? 'Share link copied' : 'Shared');
      })
      .catch(() => {
        showShareFeedback('Could not share briefing');
      });
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="py-6 lg:py-10">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center mb-8 px-4 sm:px-0">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-3">
            <span className="text-primary">Daily</span>
            <span className="text-neutral-900 dark:text-white"> Briefing</span>
          </h1>
          <p className="text-lg text-neutral-500 dark:text-neutral-400">
            Five uplifting stories to brighten your day.
          </p>
          {data?.status === 'published' && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <Button
                type="button"
                variant="outline"
                tone="primary"
                onClick={handleShareClick}
                disabled={isLoading || Boolean(error) || !activeShareIssueDate}
              >
                Share briefing
              </Button>
              {shareFeedback && (
                <span
                  className="text-sm text-neutral-500 dark:text-neutral-300"
                  role="status"
                >
                  {shareFeedback}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content area — wider */}
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Loading */}
          {isLoading && (
            <div className="space-y-6">
              <div className="animate-pulse h-8 rounded bg-neutral-200 dark:bg-neutral-700 w-64 mx-auto mb-6" />
              {/* Hero skeleton */}
              <div className="animate-pulse rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="aspect-[4/3] lg:min-h-[360px] bg-neutral-200 dark:bg-neutral-700" />
                  <div className="p-8 space-y-4">
                    <div className="h-4 w-24 rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-7 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-7 w-4/5 rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-4 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-4 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-4 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
                  </div>
                </div>
              </div>
              {/* Standard card skeletons */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse flex rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-hidden"
                  >
                    <div className="w-56 bg-neutral-200 dark:bg-neutral-700 flex-shrink-0" />
                    <div className="flex-1 p-6 space-y-3">
                      <div className="h-3 w-20 rounded bg-neutral-200 dark:bg-neutral-700" />
                      <div className="h-5 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                      <div className="h-5 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
                      <div className="h-3 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
                      <div className="h-3 w-5/6 rounded bg-neutral-200 dark:bg-neutral-700" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-lg font-semibold text-danger-600 dark:text-danger-400 mb-2">
                Failed to load the daily briefing
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                {error.message}
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Data loaded */}
          {data && !isLoading && !error && (
            <>
              <div className="border-b border-neutral-200 dark:border-neutral-700/50 mb-8">
                <BriefingDateNavigator
                  previousPublishedDate={data.previousPublishedDate ?? null}
                  nextPublishedDate={data.nextPublishedDate ?? null}
                  currentIssueDate={data.issueDate}
                />
              </div>

              {data.status === 'published' && (
                <div className="space-y-6">
                  {/* Editorial intro */}
                  {data.introText && (
                    <div className="relative pl-6 border-l-4 border-primary/40 dark:border-primary/30 py-1">
                      <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed italic">
                        {data.introText}
                      </p>
                    </div>
                  )}

                  {/* Hero story #1 */}
                  {heroStory && (
                    <BriefingStoryCard
                      story={heroStory}
                      position={heroStory.position}
                      variant="hero"
                    />
                  )}

                  {/* Stories #2-5 in 2-column grid */}
                  {remainingStories.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {remainingStories.map((story) => (
                        <BriefingStoryCard
                          key={story.id}
                          story={story}
                          position={story.position}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(data.status === 'insufficient' ||
                data.status === 'failed' ||
                data.status === 'missing') && (
                <BriefingStatusState
                  status={data.status}
                  issueDate={data.issueDate}
                  latestPublishedDate={data.latestPublishedDate ?? null}
                  previousPublishedDate={data.previousPublishedDate ?? null}
                  nextPublishedDate={data.nextPublishedDate ?? null}
                  storyCount={
                    'storyCount' in data ? data.storyCount : undefined
                  }
                  failureReason={
                    'failureReason' in data ? data.failureReason : undefined
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
